import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faChevronDown, faChartBar } from "@fortawesome/free-solid-svg-icons";
import { useQuestionnaireAnalytics } from "../../hooks/useQuestionnaireAnalytics";
import QuestionDetailPanel from "./QuestionDetailPanel";

export default function QuestionAnalysisGrid() {

    const { questions } = useQuestionnaireAnalytics();

    const METRICS = [
        { key: "avg", label: "Avg" },
        { key: "median", label: "Med" },
        { key: "total", label: "Tot" },
        { key: "min", label: "Min" },
        { key: "max", label: "Max" }
    ];

    const [selectedMetric, setSelectedMetric] = useState("avg");
    const [expandedQuestionId, setExpandedQuestionId] = useState(null);

    // Demo-safe static stats per question
    const questionStats = {
        1: {
            avg: { value: 74, delta: +3.2, trend: [68, 70, 71, 72, 73, 74] },
            median: { value: 72, delta: +2.1, trend: [67, 69, 70, 71, 71, 72] },
            total: { value: 420, delta: +1.4, trend: [380, 390, 400, 405, 415, 420] },
            min: { value: 40, delta: -0.6, trend: [42, 41, 41, 40, 40, 40] },
            max: { value: 95, delta: +0.8, trend: [92, 93, 94, 94, 95, 95] }
        },
        2: {
            avg: { value: 68, delta: -1.4, trend: [71, 70, 69, 69, 68, 68] },
            median: { value: 66, delta: -1.0, trend: [68, 67, 66, 66, 66, 66] },
            total: { value: 390, delta: -0.8, trend: [410, 405, 400, 395, 392, 390] },
            min: { value: 38, delta: -0.4, trend: [40, 39, 39, 38, 38, 38] },
            max: { value: 90, delta: +0.2, trend: [88, 89, 89, 90, 90, 90] }
        },
        3: {
            avg: { value: 61, delta: +0.9, trend: [58, 59, 60, 60, 61, 61] },
            median: { value: 60, delta: +0.7, trend: [57, 58, 59, 59, 60, 60] },
            total: { value: 360, delta: +1.1, trend: [340, 345, 350, 355, 358, 360] },
            min: { value: 35, delta: +0.3, trend: [34, 34, 35, 35, 35, 35] },
            max: { value: 85, delta: +0.5, trend: [83, 84, 84, 85, 85, 85] }
        },
		4: {
	          avg: { value: 74, delta: +3.2, trend: [68, 70, 71, 72, 73, 74] },
	          median: { value: 72, delta: +2.1, trend: [67, 69, 70, 71, 71, 72] },
	          total: { value: 420, delta: +1.4, trend: [380, 390, 400, 405, 415, 420] },
	          min: { value: 40, delta: -0.6, trend: [42, 41, 41, 40, 40, 40] },
	          max: { value: 95, delta: +0.8, trend: [92, 93, 94, 94, 95, 95] }
	      },
	      5: {
	          avg: { value: 68, delta: -1.4, trend: [71, 70, 69, 69, 68, 68] },
	          median: { value: 66, delta: -1.0, trend: [68, 67, 66, 66, 66, 66] },
	          total: { value: 390, delta: -0.8, trend: [410, 405, 400, 395, 392, 390] },
	          min: { value: 38, delta: -0.4, trend: [40, 39, 39, 38, 38, 38] },
	          max: { value: 90, delta: +0.2, trend: [88, 89, 89, 90, 90, 90] }
	      },
	      6: {
	          avg: { value: 61, delta: +0.9, trend: [58, 59, 60, 60, 61, 61] },
	          median: { value: 60, delta: +0.7, trend: [57, 58, 59, 59, 60, 60] },
	          total: { value: 360, delta: +1.1, trend: [340, 345, 350, 355, 358, 360] },
	          min: { value: 35, delta: +0.3, trend: [34, 34, 35, 35, 35, 35] },
	          max: { value: 85, delta: +0.5, trend: [83, 84, 84, 85, 85, 85] }
	      }
    };

    const columnStyles = {
        question: { flex: 2 },
        avg: { width: "60px", textAlign: "right" },
        median: { width: "70px", textAlign: "right" },
        total: { width: "70px", textAlign: "right" },
        min: { width: "60px", textAlign: "right" },
        max: { width: "60px", textAlign: "right" },
        bar: { flex: 2, padding: "0 10px" },
        delta: { width: "100px", textAlign: "right" },
        trend: { width: "60px" }
    };

    const styles = {

        container: {
            display: "flex",
            flexDirection: "column",
            padding: "10px"
        },

        metricHeaderRow: {
            display: "flex",
            alignItems: "center",
            paddingBottom: "8px",
            borderBottom: "2px solid #ddd",
            marginBottom: "6px"
        },

        questionHeader: {
            flex: 2,
            fontWeight: 600,
            fontSize: "13px"
        },

        metricControls: {
            display: "flex",
            gap: "12px"
        },

        metricHeaderCell: {
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: "pointer",
            fontSize: "12px",
            color: "#666",
            padding: "4px 8px",
            borderRadius: "4px"
        },

        metricHeaderCellActive: {
            backgroundColor: "#e8f0fe",
            color: "#4c9aff",
            fontWeight: 600
        },

        tableHeaderRow: {
            display: "flex",
            fontSize: "12px",
            fontWeight: 600,
            color: "#333",
            padding: "6px 0",
            borderBottom: "1px solid #eee"
        },

        dataRow: {
            display: "flex",
            alignItems: "center",
            padding: "6px 0",
            borderBottom: "1px solid #f1f1f1",
            fontSize: "12px",
            color: "#333"
        },

        questionCell: {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer"
        },

        barTrack: {
            height: "10px",
            backgroundColor: "#eee",
            borderRadius: "5px",
            overflow: "hidden"
        },

        barFill: {
            height: "100%",
            backgroundColor: "#4c9aff",
            borderRadius: "5px"
        },

        deltaPositive: {
            color: "#2e7d32",
            fontWeight: 600
        },

        deltaNegative: {
            color: "#c62828",
            fontWeight: 600
        },

        trend: {
            display: "flex",
            alignItems: "flex-end",
            gap: "2px",
            height: "18px"
        },

        trendBar: {
            width: "5px",
            backgroundColor: "#bbb",
            borderRadius: "2px"
        },

        trendBarLast: {
            backgroundColor: "#4c9aff"
        },

        expandedRow: {
            padding: "10px 20px",
            backgroundColor: "#fafafa",
            fontSize: "12px",
            color: "#555"
        },
		figureCell: {
		    backgroundColor: "#e2e4e7",
		    borderRadius: "6px",
		    padding: "4px 8px",
		    display: "inline-flex",
		    alignItems: "center",
		    justifyContent: "center",
		    minWidth: "40px",
		    fontWeight: 600,
		    color: "#333"
		}
    };

    function toggleExpand(questionId) {
        setExpandedQuestionId(function (prev) {
            return prev === questionId ? null : questionId;
        });
    }

    return (
        <div style={styles.container}>

            {/* Metric Selector Header */}
            <div style={styles.metricHeaderRow}>
                <div style={styles.questionHeader}>Question text</div>
                <div style={styles.metricControls}>
                    {METRICS.map(metric => (
                        <div
                            key={metric.key}
                            style={{
                                ...styles.metricHeaderCell,
                                ...(selectedMetric === metric.key ? styles.metricHeaderCellActive : {})
                            }}
                            onClick={() => setSelectedMetric(metric.key)}
                        >
                            <FontAwesomeIcon icon={faChartBar} />
                            {metric.label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Table Headers */}
            <div style={styles.tableHeaderRow}>
                <div style={columnStyles.question}>Question</div>
                <div style={columnStyles.avg}>Avg</div>
                <div style={columnStyles.median}>Med</div>
                <div style={columnStyles.total}>Tot</div>
                <div style={columnStyles.min}>Min</div>
                <div style={columnStyles.max}>Max</div>
                <div style={columnStyles.bar}>Chart</div>
                <div style={columnStyles.delta}>Change</div>
                <div style={columnStyles.trend}>Trend</div>
            </div>

            {/* Rows */}
            {questions.map(function (q) {

                const stats = questionStats[q.id];
                if (!stats) {
                    return null;
                }

                const metricData = stats[selectedMetric];
                const isExpanded = expandedQuestionId === q.id;

                return (
                    <React.Fragment key={q.id}>

                        <div style={styles.dataRow}>

                            <div style={{ ...columnStyles.question, ...styles.questionCell }}
                                 onClick={() => toggleExpand(q.id)}>
                                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                                {q.text}
                            </div>

                            <div style={columnStyles.avg}>
								<div style={styles.figureCell}>
									{stats.avg.value}
								</div>
							</div>
                            <div style={columnStyles.median}>
								<div style={styles.figureCell}>
									{stats.median.value}
								</div>
							</div>
                            <div style={columnStyles.total}>
								<div style={styles.figureCell}>
									{stats.total.value}
								</div>
							</div>
                            <div style={columnStyles.min}>
								<div style={styles.figureCell}>
									{stats.min.value}
								</div>
							</div>
                            <div style={columnStyles.max}>
								<div style={styles.figureCell}>
									{stats.max.value}
								</div>
							</div>

                            <div style={columnStyles.bar}>
                                <div style={styles.barTrack}>
                                    <div
                                        style={{
                                            ...styles.barFill,
                                            width: metricData.value + "%"
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={columnStyles.delta}>
                                <span style={metricData.delta >= 0 ? styles.deltaPositive : styles.deltaNegative}>
                                    {metricData.delta >= 0 ? "+" : ""}{metricData.delta}% ({metricData.value})
                                </span>
                            </div>

                            <div style={columnStyles.trend}>
                                <div style={styles.trend}>
                                    {metricData.trend.map((v, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                ...styles.trendBar,
                                                height: v / 5 + "px",
                                                ...(i === metricData.trend.length - 1 ? styles.trendBarLast : {})
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>

                        </div>

                        {isExpanded && (
                            <div style={styles.expandedRow}>
                               <QuestionDetailPanel questionText={q.text} />
                            </div>
                        )}

                    </React.Fragment>
                );
            })}

        </div>
    );
}
